import type {
  ACCESSAlignmentResultCode,
  ACCESSEligibilityResultCode,
  AccountData,
  MedicareCoverageData,
  RegistrationData,
  TrackType,
} from '@/types/registration';
import { toCmsGender } from '@/components/webreg/wregCmsPoll';
import { normalizeName } from '@/utils/name';
import type { WregExceptionStep } from '@/components/webreg/screens/wregExceptions';
import type { WregTrackId } from '@/components/webreg/wregScenarios';
import { EMPTY_INTAKE, type WregIntakeForm } from '@/components/webreg/screens/wregIntakeFields';
import { INVITE_STANDIN_MBI } from '@/components/webreg/wregInvite';
import {
  EMPTY_SHIPPING,
  type ShippingForm,
} from '@/components/webreg/screens/wregShippingFields';

export type WregFlowSlice = {
  eligCode: ACCESSEligibilityResultCode | null;
  alignCode: ACCESSAlignmentResultCode | null;
  switchAttested: boolean;
  intakeTerms: boolean;
  intakeGender: string;
  exit: WregExceptionStep | null;
  // Set from the eligibility poll (RND-38793). CMS answers not-eligible-already-aligned both for a
  // member another ACCESS participant aligned and for one WE aligned who dropped out before signup,
  // with nothing in the response to tell them apart; the backend matches the submitted identity
  // against its own unclaimed alignments and reports the second case here. Unrelated to
  // wregResume.ts, which restores this wizard's own position after a refresh.
  //
  // Lives in the slice rather than staying local to the hook because wregGates recomputes the step
  // from eligCode on every navigation: without it the flow sends the member to `screener` and the
  // gate immediately bounces them back.
  eligResumable: boolean;
};

export const INITIAL_WREG: WregFlowSlice = {
  eligCode: null,
  alignCode: null,
  switchAttested: false,
  intakeTerms: false,
  intakeGender: '',
  exit: null,
  eligResumable: false,
};

type WregIntakeWrites = {
  medicareCoverage: Partial<MedicareCoverageData>;
  account: Partial<AccountData>;
  wreg: Partial<WregFlowSlice>;
};

export const wregFormToContext = (f: WregIntakeForm): WregIntakeWrites => ({
  medicareCoverage: {
    firstName: normalizeName(f.first),
    lastName: normalizeName(f.last),
    dateOfBirth: f.dob,
    mbi: f.mbi,
    gender: f.gender === '' ? null : toCmsGender(f.gender),
  },
  account: {
    email: f.email.trim(),
    phoneNumber: f.phone,
  },
  wreg: {
    intakeTerms: f.tcAccepted,
    intakeGender: f.gender,
  },
});

export const wregFormFromContext = (d: RegistrationData): WregIntakeForm => {
  const { medicareCoverage: m, account: a, wreg } = d;
  if (
    m.firstName === '' &&
    m.lastName === '' &&
    m.dateOfBirth === '' &&
    m.mbi === '' &&
    a.email === '' &&
    a.phoneNumber === ''
  ) {
    return EMPTY_INTAKE;
  }
  return {
    first: m.firstName,
    last: m.lastName,
    dob: m.dateOfBirth,
    gender: wreg.intakeGender,
    mbi: m.mbi === INVITE_STANDIN_MBI ? '' : m.mbi,
    promo: d.promoCodeData?.code ?? '',
    email: a.email,
    phone: a.phoneNumber,
    tcAccepted: wreg.intakeTerms,
  };
};

const TRACK_BY_ID: Record<WregTrackId, TrackType> = {
  eckm: 'eCKM',
  ckm: 'CKM',
  bh: 'BH',
};

const ID_BY_TRACK: Record<TrackType, WregTrackId> = {
  eCKM: 'eckm',
  CKM: 'ckm',
  BH: 'bh',
};

export const wregTracksToContext = (ids: WregTrackId[]): TrackType[] =>
  ids.map((id) => TRACK_BY_ID[id]);

export const wregTracksFromContext = (tracks: TrackType[]): WregTrackId[] =>
  tracks.map((track) => ID_BY_TRACK[track]);

export const wregShippingFromContext = (d: RegistrationData): ShippingForm => {
  const s = d.shipping;
  if (s.streetAddress === '' && s.city === '' && s.state === '' && s.zipCode === '') {
    return EMPTY_SHIPPING;
  }
  return {
    first: s.firstName,
    last: s.lastName,
    street: s.streetAddress,
    apt: s.apartment,
    city: s.city,
    state: s.state,
    zip: s.zipCode,
  };
};

export const wregHasShipped = (d: RegistrationData): boolean => d.shipping.streetAddress !== '';
